import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  MapPin, Users, Plus, Pencil, Trash2, Loader2, Target,
  RotateCcw, Shield, Globe, Building2
} from 'lucide-react';

const ROUTING_METHODS = [
  { value: 'round_robin', label: 'Round Robin' },
  { value: 'weighted', label: 'Weighted (Fewest Leads)' },
  { value: 'manual', label: 'Manual Assignment' },
];

export default function Territories() {
  const [territories, setTerritories] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', regions: '', industries: '',
    deal_size_min: '', deal_size_max: '', assigned_reps: [], routing_method: 'round_robin'
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [terRes, teamRes] = await Promise.all([
        api.get('/api/territories/'),
        api.get('/api/team/members').catch(() => ({ data: { data: [] } }))
      ]);
      setTerritories(terRes.data.data || []);
      setTeamUsers(teamRes.data.data || teamRes.data || []);
    } catch (e) {
      toast.error('Failed to load territories');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ name: '', description: '', regions: '', industries: '', deal_size_min: '', deal_size_max: '', assigned_reps: [], routing_method: 'round_robin' });
    setEditId(null);
    setShowForm(false);
  };

  const handleEdit = (t) => {
    setForm({
      name: t.name,
      description: t.description || '',
      regions: (t.regions || []).join(', '),
      industries: (t.industries || []).join(', '),
      deal_size_min: t.deal_size_min || '',
      deal_size_max: t.deal_size_max || '',
      assigned_reps: t.assigned_reps || [],
      routing_method: t.routing_method || 'round_robin',
    });
    setEditId(t.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Territory name is required'); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      regions: form.regions ? form.regions.split(',').map(s => s.trim()).filter(Boolean) : [],
      industries: form.industries ? form.industries.split(',').map(s => s.trim()).filter(Boolean) : [],
      deal_size_min: form.deal_size_min ? parseFloat(form.deal_size_min) : null,
      deal_size_max: form.deal_size_max ? parseFloat(form.deal_size_max) : null,
      assigned_reps: form.assigned_reps,
      routing_method: form.routing_method,
    };
    try {
      if (editId) {
        await api.put(`/api/territories/${editId}`, payload);
        toast.success('Territory updated');
      } else {
        await api.post('/api/territories/', payload);
        toast.success('Territory created');
      }
      resetForm();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save territory');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this territory?')) return;
    try {
      await api.delete(`/api/territories/${id}`);
      toast.success('Territory deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const toggleRep = (repId) => {
    setForm(f => ({
      ...f,
      assigned_reps: f.assigned_reps.includes(repId)
        ? f.assigned_reps.filter(id => id !== repId)
        : [...f.assigned_reps, repId]
    }));
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4" data-testid="territories-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="territories-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Territories</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Define sales territories and auto-route leads to the right reps.
          </p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="create-territory-btn">
          <Plus className="h-4 w-4 mr-2" /> New Territory
        </Button>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <Card data-testid="territory-form">
          <CardHeader>
            <CardTitle className="text-base">{editId ? 'Edit Territory' : 'Create Territory'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  data-testid="territory-name"
                  placeholder="e.g., US West Coast"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  data-testid="territory-description"
                  placeholder="Optional description"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-1"><Globe className="h-3 w-3" /> Regions (comma-separated)</Label>
                <Input
                  data-testid="territory-regions"
                  placeholder="US, CA, UK, EMEA"
                  value={form.regions}
                  onChange={e => setForm(f => ({ ...f, regions: e.target.value }))}
                />
              </div>
              <div>
                <Label className="flex items-center gap-1"><Building2 className="h-3 w-3" /> Industries (comma-separated)</Label>
                <Input
                  data-testid="territory-industries"
                  placeholder="SaaS, Healthcare, FinTech"
                  value={form.industries}
                  onChange={e => setForm(f => ({ ...f, industries: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Min Deal Size ($)</Label>
                <Input
                  data-testid="territory-min-deal"
                  type="number" placeholder="10000"
                  value={form.deal_size_min}
                  onChange={e => setForm(f => ({ ...f, deal_size_min: e.target.value }))}
                />
              </div>
              <div>
                <Label>Max Deal Size ($)</Label>
                <Input
                  data-testid="territory-max-deal"
                  type="number" placeholder="500000"
                  value={form.deal_size_max}
                  onChange={e => setForm(f => ({ ...f, deal_size_max: e.target.value }))}
                />
              </div>
              <div>
                <Label>Routing Method</Label>
                <Select value={form.routing_method} onValueChange={v => setForm(f => ({ ...f, routing_method: v }))}>
                  <SelectTrigger data-testid="territory-routing-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROUTING_METHODS.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Rep Assignment */}
            {teamUsers.length > 0 && (
              <div>
                <Label className="flex items-center gap-1 mb-2"><Users className="h-3 w-3" /> Assign Reps</Label>
                <div className="flex flex-wrap gap-2">
                  {teamUsers.map(u => (
                    <Button
                      key={u.id}
                      size="sm"
                      variant={form.assigned_reps.includes(u.id) ? 'default' : 'outline'}
                      onClick={() => toggleRep(u.id)}
                      data-testid={`territory-rep-${u.id}`}
                    >
                      {u.name || u.email}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving} data-testid="territory-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editId ? 'Update' : 'Create'} Territory
              </Button>
              <Button variant="outline" onClick={resetForm}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Territory List */}
      {territories.length > 0 ? (
        <div className="space-y-3" data-testid="territories-list">
          {territories.map(t => (
            <Card key={t.id} data-testid={`territory-${t.id}`}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-blue-500" />
                      <span className="font-medium">{t.name}</span>
                      <Badge variant={t.is_active ? 'default' : 'secondary'}>
                        {t.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        <RotateCcw className="h-3 w-3 mr-1" />
                        {ROUTING_METHODS.find(m => m.value === t.routing_method)?.label || t.routing_method}
                      </Badge>
                    </div>
                    {t.description && <p className="text-sm text-muted-foreground mt-1">{t.description}</p>}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(t.regions || []).map(r => (
                        <Badge key={r} variant="outline" className="text-xs"><Globe className="h-3 w-3 mr-1" />{r}</Badge>
                      ))}
                      {(t.industries || []).map(ind => (
                        <Badge key={ind} variant="outline" className="text-xs"><Building2 className="h-3 w-3 mr-1" />{ind}</Badge>
                      ))}
                      {t.deal_size_min != null && (
                        <Badge variant="outline" className="text-xs">
                          ${(t.deal_size_min || 0).toLocaleString()} - ${(t.deal_size_max || 0).toLocaleString()}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Users className="h-3 w-3" />{(t.assigned_reps || []).length} reps</span>
                      <span className="flex items-center gap-1"><Target className="h-3 w-3" />{t.leads_routed || 0} leads routed</span>
                    </div>
                    {(t.rep_details || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {t.rep_details.map(rep => (
                          <Badge key={rep.id} variant="secondary" className="text-xs">
                            {rep.name || rep.email} ({rep.active_leads} active)
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(t)} data-testid={`edit-territory-${t.id}`}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(t.id)} data-testid={`delete-territory-${t.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : !showForm && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <MapPin className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No territories defined. Create one to start auto-routing leads.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
